import { Button } from "@a2z/ui";

import { useTranslations } from "@/lib/i18n/intl";
import { getLocaleDirection, keys } from "@/lib/i18n/portal";
import type { PortalLocale } from "@/lib/i18n/portal";
import { getRestaurantAppUrl } from "@/lib/routing/portal-urls";
import { LogoutButton } from "@/shared/layout/LogoutButton";
import { Surface } from "@/shared/layout/Surface";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import type { SellerAvailableStoreView } from "@/lib/api/types";

type RestaurantStoreTerminalProps = {
  locale: PortalLocale;
  storeSlug: string;
  availableStores: SellerAvailableStoreView[];
};

/**
 * Decision D13 — the seller portal does not open for a RESTAURANT store.
 *
 * A restaurant is administered **only** in `apps/restaurant`. Letting an owner
 * half-use this portal for one would mean two places to edit a menu, two places
 * that disagree, and a product surface built for shipped orders being pointed at
 * a dine-in business.
 *
 * **This is a terminal screen, not a hidden nav.** It is rendered by the
 * protected layouts *instead of* the app shell, so a deep link straight to
 * `/en/<slug>/orders` lands here too. Hiding nav items would leave every route
 * reachable by URL and the portal half-usable — which is precisely what the
 * decision exists to prevent.
 *
 * Two things stay reachable on purpose:
 *
 * - **The store switcher**, when the account has somewhere else to go. Seller
 *   login binds `stores[0]`, so a multi-store owner can land here through no
 *   fault of their own; a screen with no way out would be a dead end rather
 *   than an explanation.
 * - **Logout**, always. Nobody should have to clear cookies to leave a screen.
 *
 * This is a **frontend** rule. `/api/seller/**` stays open to restaurant stores
 * deliberately: `apps/restaurant` consumes those endpoints for its own session,
 * uploads, categories, staff/roles, profile and billing. What the rule buys is
 * that nobody *works* in the wrong app — not that the API is closed.
 */
export function RestaurantStoreTerminal({
  locale,
  storeSlug,
  availableStores,
}: RestaurantStoreTerminalProps) {
  const t = useTranslations();
  const { isSwitching, error, switchTo } = useStoreSwitch(locale, storeSlug);

  // Non-restaurant stores only. This screen exists because the bound store is
  // a restaurant; offering another restaurant would switch and land right back
  // here, which is a loop rather than a way out.
  const otherStores = availableStores.filter(
    (store) =>
      store.storeAccess !== false && store.storeSlug !== storeSlug && store.storeKind !== "RESTAURANT",
  );

  return (
    <Surface
      locale={locale}
      direction={getLocaleDirection(locale)}
      title={t(keys.seller.restaurantStoreTitle)}
      subtitle={t(keys.seller.restaurantStoreBody)}
    >
      <div className="flex flex-col gap-6">
        <a
          href={getRestaurantAppUrl(locale)}
          className="inline-flex h-11 items-center justify-center rounded-full bg-[color:var(--portal-accent)] px-5 text-sm font-semibold text-white transition hover:opacity-90"
        >
          {t(keys.seller.restaurantStoreCta)}
        </a>

        {otherStores.length > 0 ? (
          <section className="flex flex-col gap-3">
            <div>
              <h2 className="text-sm font-semibold">
                {t(keys.seller.restaurantStoreSwitchTitle)}
              </h2>
              <p className="text-sm text-[color:var(--portal-muted)]">
                {t(keys.seller.restaurantStoreSwitchHint)}
              </p>
            </div>
            <ul className="flex flex-col gap-2">
              {otherStores.map((store) => (
                <li key={store.storeId}>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSwitching}
                    onClick={() => {
                      void switchTo(store.storeSlug);
                    }}
                    className="h-11 w-full justify-between rounded-full px-4 text-sm font-semibold"
                    data-testid={`restaurant-terminal-switch-${store.storeSlug}`}
                  >
                    {store.storeName}
                  </Button>
                </li>
              ))}
            </ul>
            {error ? (
              <p role="alert" className="text-sm text-[color:var(--portal-danger)]">
                {error}
              </p>
            ) : null}
          </section>
        ) : null}

        <LogoutButton redirectTo={`/${locale}/login`} />
      </div>
    </Surface>
  );
}
